import { useMemo, useState, type JSX } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import ModalWithoutHeaderAndFooterAtCenter from "@/components/common/ModalWithoutHeaderAndFooterAtCenter/ModalWithoutHeaderAndFooterAtCenter";
import { MAX_APPLIED_SEGMENTS } from "@/components/projectCommon/Filters/reducer/appliedSegmentsReducer";
import type { SelectionPartition } from "@/utils/selections/partitionSelections";
import type { TruncatedKinds } from "@/utils/selections/buildValidateRequest";
import { buildLedger, type LedgerRow } from "./ledger/buildLedger";
import LedgerGroup from "./ledger/LedgerGroup";
import { countSelections, modalCopy } from "./modalCopy";

export interface SelectionAdoptionModalProps {
  open: boolean;
  partition: SelectionPartition;
  truncated: TruncatedKinds;
  /** Remaining applied-segment slots. Undefined when no cap applies. */
  segmentHeadroom?: number;
  onClose: () => void;
  onAdd: (brandSetUlids: string[], segmentCodes: string[]) => void;
  isApplying: boolean;
  errorMessage: string | null;
}

// The truncation notice's kind list is a closed set of at most four
// ("brands", "brand sets", "filters", "segments") — unlike a member-name
// list, it never needs a cap, so it gets its own uncapped conjunction join
// rather than reusing formatNameList's "N others" collapse, which would
// turn a fully-truncated link into a meaningless "...and 2 others".
const joinKinds = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

const LinkIcon = (): JSX.Element => (
  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.4 11.6a2.9 2.9 0 0 0 4.2 0l2.4-2.4a2.9 2.9 0 0 0-4.1-4.1l-1 1" />
      <path d="M11.6 8.4a2.9 2.9 0 0 0-4.2 0L5 10.8a2.9 2.9 0 0 0 4.1 4.1l1-1" />
    </g>
  </svg>
);

const SelectionAdoptionModal = ({
  open, partition, truncated, segmentHeadroom, onClose, onAdd, isApplying, errorMessage,
}: SelectionAdoptionModalProps): JSX.Element => {
  // The validate response carries no brand logo or display name, so brand rows
  // resolve against the caller's own catalog to render the same marks the rest
  // of the app does.
  const brandEntities = useSelector(
    (s: RootState) => s.activeGroup.brands.entities,
  );
  const groups = useMemo(
    () => buildLedger(partition, { brandEntities }),
    [partition, brandEntities],
  );
  const copy = useMemo(() => modalCopy(partition), [partition]);
  const counts = useMemo(() => countSelections(partition), [partition]);

  const setUlids = partition.adoptable.brandSets.map((c) => c.ulid);
  const segmentCodes = partition.adoptable.segments.map((c) => c.code);

  // Pre-check everything that fits. When headroom is tighter than the number of
  // adoptable segments, the overflow starts unchecked rather than being checked
  // and then refused at submit.
  const [checkedIds, setCheckedIds] = useState<string[]>(() => [
    ...setUlids,
    ...(typeof segmentHeadroom === "number"
      ? segmentCodes.slice(0, Math.max(0, segmentHeadroom))
      : segmentCodes),
  ]);

  const checkedSegmentCount = checkedIds.filter((id) => segmentCodes.includes(id)).length;
  const capReached =
    typeof segmentHeadroom === "number" && checkedSegmentCount >= segmentHeadroom;

  const isDisabled = (row: LedgerRow): boolean => {
    if (isApplying) return true;
    if (row.kind !== "segment" || !row.adoptId) return false;
    return capReached && !checkedIds.includes(row.adoptId);
  };

  const toggle = (id: string): void =>
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const showSupportLine = groups.some((g) => g.rows.some((r) => r.entitlement));

  // joinKinds joins with the right conjunction, so two kinds read
  // "brand sets and segments".
const truncatedLabels = (
    [
      [truncated.brands, "brands"],
      [truncated.brandSets, "brand sets"],
      [truncated.filters, "filters"],
      [truncated.segments, "segments"],
    ] as const
  )
    .filter(([hit]) => hit)
    .map(([, label]) => label);
  const nothingChecked = checkedIds.length === 0;

  return (
    <ModalWithoutHeaderAndFooterAtCenter
      open={open}
      onClose={isApplying ? () => {} : onClose}
      className="modal-elevated"
    >
      <div className="w-[560px] max-w-[92vw] flex flex-col max-h-[88vh]">
        <div className="relative shrink-0 px-[26px] pt-5 pb-4 border-b border-neutral-200 bg-neutral-50">
          <div className="flex items-center gap-[7px] mb-[9px] text-text-50">
            <LinkIcon />
            <span className="text-[11px] font-semibold uppercase tracking-[0.07em]">Shared link</span>
          </div>
          <h2 className="text-[17px] font-bold text-text-950 tracking-[-0.022em] leading-[1.25] pr-[34px]">
            {copy.title}
          </h2>
          <p className="text-[13px] text-text-50 mt-[5px] leading-[1.5] max-w-[430px]">{copy.subline}</p>
          <button
            type="button"
            aria-label="Close"
            disabled={isApplying}
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 rounded-[7px] flex items-center justify-center text-text-50 transition hover:bg-neutral-100 hover:text-text-950 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          >
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="px-[26px] pt-1 pb-[18px] flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {groups.map((group) => (
            <LedgerGroup
              key={group.kind}
              group={group}
              checkedIds={checkedIds}
              isDisabled={isDisabled}
              disabledReason={
                group.kind === "segment" && !isApplying && typeof segmentHeadroom === "number"
                  ? `You can apply up to ${MAX_APPLIED_SEGMENTS} segments at a time.`
                  : undefined
              }
              onToggle={toggle}
            />
          ))}
        </div>

        {typeof segmentHeadroom === "number" && segmentHeadroom < segmentCodes.length ? (
          <div className="px-[26px] pb-2 text-xs text-text-50 tnum">
            {segmentHeadroom} of {MAX_APPLIED_SEGMENTS} slots left
          </div>
        ) : null}

        {truncatedLabels.length ? (
          <div className="px-[26px] pb-2 text-xs text-text-50">
            This link contained more {joinKinds(truncatedLabels)} than we can check at once,
            so some aren't shown.
          </div>
        ) : null}

        {showSupportLine ? (
          <div className="px-[26px] py-3 border-t border-neutral-200 bg-neutral-25">
            <p className="text-[12px] text-text-300 leading-[1.55]">
              To add these to your plan, contact your customer success representative about
              extending your contract.
            </p>
          </div>
        ) : null}

        {errorMessage ? (
          <div role="status" aria-live="polite" className="px-[26px] pt-3 text-xs text-warning-500">
            {errorMessage}
          </div>
        ) : null}

        {/* Footer is test-addressable because an unchecked row pill and the
            primary button share the accessible name "Add". */}
        <div
          data-testid="adoption-footer"
          className="px-[26px] py-3 border-t border-neutral-200 bg-neutral-50 flex items-center gap-[9px]"
        >
          <span className="flex-1 text-[12px] text-text-50 tnum">
            {copy.showFooterCount
              ? `${checkedIds.length} of ${counts.total} items in this link will be added`
              : ""}
          </span>
          {copy.primaryLabel === null ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-[9px] cursor-pointer border border-neutral-200 bg-white px-[15px] py-[9px] text-[13px] tracking-[-0.005em] font-semibold text-text-300 transition hover:bg-neutral-25 hover:border-neutral-300"
            >
              {copy.dismissLabel}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={isApplying}
                className="rounded-[9px] cursor-pointer border border-neutral-200 bg-white px-[15px] py-[9px] text-[13px] tracking-[-0.005em] font-semibold text-text-300 transition hover:bg-neutral-25 hover:border-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copy.dismissLabel}
              </button>
              <button
                type="button"
                disabled={nothingChecked || isApplying}
                onClick={() =>
                  onAdd(
                    checkedIds.filter((id) => setUlids.includes(id)),
                    checkedIds.filter((id) => segmentCodes.includes(id)),
                  )
                }
                className="rounded-[9px] cursor-pointer bg-primary-500 px-[15px] py-[9px] text-[13px] tracking-[-0.005em] font-semibold text-white shadow-[0_1px_2px_rgba(20,22,28,0.12)] transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isApplying ? "Adding…" : copy.primaryLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalWithoutHeaderAndFooterAtCenter>
  );
};

export default SelectionAdoptionModal;
